import { supabase, handleSupabaseError } from '../lib/supabase';

export const authService = {
  // Sign up with email (for initial setup)
  signUp: async (email, password, userData) => {
    try {
      const redirectUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:3000/auth/callback'
        : `${window.location.origin}/auth/callback`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: userData.full_name,
            business_name: userData.business_name
          },
          emailRedirectTo: redirectUrl
        }
      });

      if (error) throw error;
      return { data, success: true };
    } catch (error) {
      throw new Error(handleSupabaseError(error));
    }
  },

  // Sign in with email
  signIn: async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      return { data, success: true };
    } catch (error) {
      throw new Error(handleSupabaseError(error));
    }
  },

  // Get current session
  getSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    } catch (error) {
      console.error('Get session error:', error);
      return null;
    }
  },

  // Get current user with profile
  getCurrentUser: async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      
      if (!user) return null;

      // Get user profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      return {
        ...user,
        profile
      };
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  },

  // Update user profile
  updateProfile: async (updates) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;

      // Keep AuthContext + local cache in sync immediately after writes.
      try {
        if (typeof localStorage !== 'undefined') {
          const cacheData = { ...data, _cacheTimestamp: Date.now() };
          localStorage.setItem(`profile_${user.id}`, JSON.stringify(cacheData));
        }
      } catch (e) {
        // Non-fatal: cache write can fail in private mode or quota limits
      }

      try {
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(
            new CustomEvent('quotetier:profile-updated', {
              detail: { userId: user.id, profile: data },
            })
          );
        }
      } catch (e) {
        // Non-fatal
      }

      return { data, success: true };
    } catch (error) {
      throw new Error(handleSupabaseError(error));
    }
  },

  // Sign out
  signOut: async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { success: true };
    } catch (error) {
      throw new Error(handleSupabaseError(error));
    }
  },

  // Listen to auth state changes
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback);
  }
};
